import { useState } from 'react';
import { ChevronLeft, Pause, X, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  useJob,
  useFasesCatalog,
  useCancelAutomation,
  type JobSnapshot,
} from '@/lib/hooks/prestacoes';
import { PrestTimeline } from './PrestTimeline';

interface PrestacaoActiveModuleProps {
  prestacaoId: string;
  workspaceId: string | null;
  onBack: () => void;
}

/** Banner D2 — AGUARDANDO_HUMANO no mobile → instrução para ir ao computador */
function AguardandoHumanoBanner() {
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 14px',
        borderRadius: 'var(--r-3)',
        background: 'var(--warn-soft)',
        border: '1px solid var(--warn)',
      }}
    >
      <span
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: 'var(--warn)',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Monitor size={18} strokeWidth={1.8} />
      </span>
      <div>
        <p
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: 'var(--fg)',
            margin: '0 0 4px',
          }}
        >
          Acesse no computador para finalizar
        </p>
        <p
          style={{
            fontSize: 12,
            color: 'var(--fg-muted)',
            margin: 0,
            lineHeight: 1.5,
          }}
        >
          Todas as fases foram preenchidas. O botão "Concluir" fica disponível
          apenas no computador — clique lá para enviar ao portal Suzano.
        </p>
      </div>
    </div>
  );
}

/** Progress bar mint com track shadow-pressed */
function PhaseProgress({
  current,
  total,
  percent,
}: {
  current: number;
  total: number;
  percent: number;
}) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 'var(--r-3)',
        padding: 14,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 8,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            color: 'var(--mint-deep)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            fontWeight: 700,
          }}
        >
          FASE {current} DE {total}
        </span>
        <span
          style={{
            fontSize: 11,
            fontFamily: 'var(--font-mono)',
            color: 'var(--fg-muted)',
          }}
        >
          {percent}%
        </span>
      </div>

      {/* Track com shadow-pressed */}
      <div
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Progresso: fase ${current} de ${total}`}
        style={{
          height: 6,
          borderRadius: 'var(--r-pill)',
          background: 'var(--shadow-pressed, var(--card-soft))',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${percent}%`,
            borderRadius: 'var(--r-pill)',
            background: 'linear-gradient(90deg, var(--mint) 0%, var(--mint-deep) 100%)',
            transition: 'width 0.4s ease',
          }}
        />
      </div>
    </div>
  );
}

/** Stream IA placeholder — integração SSE real é apenas no desktop */
function AIStreamPlaceholder({ currentFase }: { currentFase: number | null }) {
  return (
    <div
      style={{
        background: 'var(--card)',
        border: '1px solid var(--card-stroke)',
        borderRadius: 'var(--r-3)',
        padding: 14,
      }}
      aria-live="polite"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: 'var(--mint-deep)',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
          aria-hidden
        />
        <span
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontWeight: 700,
            color: 'var(--fg-muted)',
          }}
        >
          IA em operação
        </span>
      </div>
      <p
        style={{
          fontSize: 12,
          color: 'var(--fg-muted)',
          margin: 0,
          lineHeight: 1.5,
          fontFamily: 'var(--font-mono)',
        }}
      >
        {currentFase
          ? `Executando fase ${currentFase}… Acompanhe o progresso detalhado no computador.`
          : 'Aguardando início da próxima fase…'}
      </p>
    </div>
  );
}

/**
 * Módulo Prestação Ativa — read-only no mobile (Decisão D2).
 * Mostra progresso, timeline e stream IA (placeholder).
 * SEM iframe Steel. SEM botão Concluir.
 */
export function PrestacaoActiveModule({
  prestacaoId,
  workspaceId,
  onBack,
}: PrestacaoActiveModuleProps) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmPause, setConfirmPause] = useState(false);

  const fasesQ = useFasesCatalog(workspaceId);
  const jobQ = useJob(workspaceId, prestacaoId);
  const cancelMut = useCancelAutomation(workspaceId);

  const job = jobQ.data && 'status' in jobQ.data ? (jobQ.data as JobSnapshot) : null;
  const fasesCatalog = fasesQ.data?.items ?? [];
  const totalFases = fasesCatalog.length || 16;

  const currentFase = job?.currentFase ?? null;
  const doneCount = job?.fases.filter((f) => f.status === 'DONE').length ?? 0;
  const percent = totalFases > 0 ? Math.round((doneCount / totalFases) * 100) : 0;

  const isAguardando = job?.status === 'WAITING_HUMAN';
  const isRunning = job?.status === 'RUNNING' || job?.status === 'QUEUED';

  async function handleCancel() {
    try {
      await cancelMut.mutateAsync(prestacaoId);
      toast.success('Automação cancelada.');
      setConfirmCancel(false);
      onBack();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cancelar.');
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: '1fr auto',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Corpo scrollável */}
      <div
        style={{
          overflowY: 'auto',
          padding: '8px 18px 18px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Header com back */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 4,
          }}
        >
          <button
            type="button"
            onClick={onBack}
            aria-label="Voltar para lista de prestações"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              color: 'var(--fg-muted)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 0',
              minHeight: 44,
            }}
          >
            <ChevronLeft size={15} strokeWidth={1.8} aria-hidden />
            Prestações
          </button>

          {/* Status pill */}
          {job && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '4px 10px',
                borderRadius: 'var(--r-pill)',
                fontSize: 10.5,
                fontWeight: 700,
                fontFamily: 'var(--font-mono)',
                background: isAguardando ? 'var(--warn-soft)' : 'var(--mint-soft)',
                color: isAguardando ? 'var(--warn)' : 'var(--mint-deep)',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: isAguardando ? 'var(--warn)' : 'var(--mint-deep)',
                  animation: isRunning ? 'pulse 1.5s ease-in-out infinite' : undefined,
                }}
                aria-hidden
              />
              {isAguardando ? 'aguardando você' : isRunning ? 'em automação' : job.status.toLowerCase()}
            </span>
          )}
        </div>

        {/* Banner D2 — aguardando humano */}
        {isAguardando && <AguardandoHumanoBanner />}

        {/* Progresso */}
        <PhaseProgress
          current={currentFase ?? doneCount}
          total={totalFases}
          percent={percent}
        />

        {/* Stream IA placeholder */}
        <AIStreamPlaceholder currentFase={currentFase} />

        {/* Timeline das 16 fases */}
        <div
          style={{
            background: 'var(--card)',
            border: '1px solid var(--card-stroke)',
            borderRadius: 'var(--r-3)',
            padding: 14,
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontWeight: 600,
              color: 'var(--fg-muted)',
              marginBottom: 12,
            }}
          >
            Fases · {doneCount}/{totalFases} concluídas
          </div>
          <PrestTimeline
            fases={fasesCatalog}
            faseLogs={job?.fases ?? []}
            currentFase={currentFase}
          />
        </div>
      </div>

      {/* Action bar fixa (somente se automação em curso) */}
      {isRunning && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr auto',
            gap: 8,
            padding: '10px 16px 24px',
            background: 'var(--frame)',
            borderTop: '1px solid var(--card-stroke)',
          }}
        >
          <button
            type="button"
            onClick={onBack}
            aria-label="Voltar"
            style={{
              width: 44,
              height: 44,
              borderRadius: 'var(--r-2)',
              background: 'var(--card-soft)',
              border: '1px solid var(--card-stroke)',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--fg-muted)',
              cursor: 'pointer',
            }}
          >
            <ChevronLeft size={16} strokeWidth={1.8} aria-hidden />
          </button>

          <button
            type="button"
            onClick={() => setConfirmPause(true)}
            aria-label="Pausar automação"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              height: 44,
              borderRadius: 'var(--r-2)',
              background: 'var(--card-soft)',
              border: '1px solid var(--card-stroke)',
              color: 'var(--fg)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <Pause size={14} strokeWidth={1.8} aria-hidden />
            Pausar
          </button>

          <button
            type="button"
            onClick={() => setConfirmCancel(true)}
            aria-label="Cancelar automação"
            style={{
              width: 44,
              height: 44,
              borderRadius: 'var(--r-2)',
              background: 'var(--warn-soft)',
              border: '1px solid var(--warn)',
              display: 'grid',
              placeItems: 'center',
              color: 'var(--block)',
              cursor: 'pointer',
            }}
          >
            <X size={16} strokeWidth={1.8} aria-hidden />
          </button>
        </div>
      )}

      {/* Confirm cancelar */}
      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        tone="danger"
        title="Cancelar automação?"
        description="A sessão de automação será encerrada e a prestação voltará ao estado anterior. Você poderá iniciar novamente depois."
        confirmLabel="Cancelar automação"
        loading={cancelMut.isPending}
        onConfirm={handleCancel}
      />

      {/* Confirm pausar — pausa não existe na API ainda; exibe aviso */}
      <ConfirmDialog
        open={confirmPause}
        onOpenChange={setConfirmPause}
        title="Pausar automação?"
        description="A pausa não está disponível nesta versão. Para interromper a automação, use o botão Cancelar."
        confirmLabel="Entendido"
        cancelLabel="Fechar"
        onConfirm={() => setConfirmPause(false)}
      />
    </div>
  );
}
